import { __assign, __extends, __rest } from "tslib";
import * as React from 'react';
import { TouchableWithoutFeedback, View, StyleSheet } from 'react-native';
import color from 'color';
import defaultTheme from '../styles/defaultTheme';
import { withOpacity } from '../v2/withOpacity';
/**
 * A wrapper for views that should respond to touches.
 * Provides a material "ink ripple" interaction effect for supported platforms (>= Android Lollipop).
 * On unsupported platforms, it falls back to a highlight effect.
 *
 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { View } from 'react-native';
 * import { PnmacText, PnmacTouchableRipple } from 'pnmac-universal-components';
 *
 * const MyComponent = () => (
 *   <PnmacTouchableRipple
 *     onPress={() => console.log('Pressed')}
 *     rippleColor="rgba(0, 0, 0, .32)"
 *   >
 *     <PnmacText>Press me</PnmacText>
 *   </PnmacTouchableRipple>
 * );
 *
 * export default MyComponent;
 * ```
 */
var PnmacTouchableRipple = /** @class */function (_super) {
  __extends(PnmacTouchableRipple, _super);
  function PnmacTouchableRipple() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.handlePressIn = function (e) {
      var _a = _this.props,
        centered = _a.centered,
        rippleColor = _a.rippleColor,
        onPressIn = _a.onPressIn,
        theme = _a.theme;
      if (onPressIn) {
        onPressIn(e);
      }
      var calculatedRippleColor = rippleColor || withOpacity("#003087", 0.2);
      var button = e.currentTarget;
      var style = window.getComputedStyle(button);
      var dimensions = button.getBoundingClientRect();
      var touchX;
      var touchY;
      if (centered) {
        touchX = dimensions.width / 2;
        touchY = dimensions.height / 2;
      } else {
        var startX = e.nativeEvent.touches ? e.nativeEvent.touches[0].pageX : e.pageX;
        var startY = e.nativeEvent.touches ? e.nativeEvent.touches[0].pageY : e.pageY;
        touchX = startX - dimensions.left;
        touchY = startY - dimensions.top;
      }
      // Get the size of the button to determine how big the ripple should be
      var size = centered ?
      // If ripple is always centered, we don't need to make it too big
      Math.min(dimensions.width, dimensions.height) * 1.25 :
      // Otherwise make it twice as big so clicking on one end spreads ripple to other
      Math.max(dimensions.width, dimensions.height) * 2;
      // Create a container for our ripple effect so we don't need to change the parent's style
      var container = document.createElement('span');
      container.setAttribute('data-paper-ripple', '');
      Object.assign(container.style, {
        position: 'absolute',
        pointerEvents: 'none',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        borderTopLeftRadius: style.borderTopLeftRadius,
        borderTopRightRadius: style.borderTopRightRadius,
        borderBottomRightRadius: style.borderBottomRightRadius,
        borderBottomLeftRadius: style.borderBottomLeftRadius,
        overflow: centered ? 'visible' : 'hidden'
      });
      // Create span to show the ripple effect
      var ripple = document.createElement('span');
      Object.assign(ripple.style, {
        position: 'absolute',
        pointerEvents: 'none',
        backgroundColor: calculatedRippleColor,
        borderRadius: '50%',
        /* Transition configuration */
        transitionProperty: 'transform opacity',
        transitionDuration: "".concat(Math.min(size * 1.5, 350), "ms"),
        transitionTimingFunction: 'linear',
        transformOrigin: 'center',
        /* We'll animate these properties */
        transform: 'translate3d(-50%, -50%, 0) scale3d(0.1, 0.1, 0.1)',
        opacity: '0.5',
        // Position the ripple where cursor was
        left: "".concat(touchX, "px"),
        top: "".concat(touchY, "px"),
        width: "".concat(size, "px"),
        height: "".concat(size, "px")
      });
      // Finally, append it to DOM
      container.appendChild(ripple);
      button.appendChild(container);
      // rAF runs in the same frame as the event handler
      // Use double rAF to ensure the transition class is added in next frame
      // This will make sure that the transition animation is triggered
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          Object.assign(ripple.style, {
            transform: 'translate3d(-50%, -50%, 0) scale3d(1, 1, 1)',
            opacity: '1'
          });
        });
      });
    };
    _this.handlePressOut = function (e) {
      _this.props.onPressOut && _this.props.onPressOut(e);
      var containers = e.currentTarget.querySelectorAll('[data-paper-ripple]');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          containers.forEach(function (container) {
            // @ts-ignore
            var ripple = container.firstChild;
            // @ts-ignore
            Object.assign(ripple.style, {
              transitionDuration: '250ms',
              opacity: 0
            });
            // Finally remove the span after the transition
            setTimeout(function () {
              // @ts-ignore
              var parentNode = container.parentNode;
              if (parentNode) {
                parentNode.removeChild(container);
              }
            }, 500);
          });
        });
      });
    };
    return _this;
  }
  PnmacTouchableRipple.prototype.render = function () {
    var _a = this.props,
      style = _a.style,
      background = _a.background,
      borderless = _a.borderless,
      disabledProp = _a.disabled,
      rippleColor = _a.rippleColor,
      underlayColor = _a.underlayColor,
      children = _a.children,
      theme = _a.theme,
      rest = __rest(_a, ["style", "background", "borderless", "disabled", "rippleColor", "underlayColor", "children", "theme"]);
    var disabled = disabledProp || !this.props.onPress;
    return /*#__PURE__*/React.createElement(TouchableWithoutFeedback, __assign({}, rest, {
      accessible: true,
      onPressIn: this.handlePressIn,
      onPressOut: this.handlePressOut,
      disabled: disabled
    }), /*#__PURE__*/React.createElement(View, {
      style: [styles.touchable, borderless && styles.borderless, style]
    }, React.Children.only(children)));
  };
  PnmacTouchableRipple.defaultProps = {
    borderless: false
  };
  PnmacTouchableRipple.supported = true;
  return PnmacTouchableRipple;
}(React.Component);
var styles = StyleSheet.create({
  touchable: {
    position: 'relative'
  },
  borderless: {
    overflow: 'hidden'
  }
});
export default defaultTheme(PnmacTouchableRipple);